import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { PropertiesService } from 'src/app/pages/properties/services/properties.services';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { ChatPropertyPick } from '../../chat.model';

/** Filtros que o seletor recebe já feitos (a qualificação do cliente). */
export interface ChatPropertyPickerPreset {
  typology?: number[];
  price?: { min?: number; max?: number };
  businessTypes?: number[];
  propertyTypeGroups?: number[];
}

/** O âmbito da lista: só os meus, a minha agência (lojas) ou toda a rede (entidade). */
export type ChatPropertyScope = 'mine' | 'store' | 'network';

interface Option {
  id: number;
  name: string;
}

/** "T5+" vale 5 ou mais: o filtro do back-end só trata 10 como "mais de 10". */
const TYPOLOGY_OPTIONS: Option[] = [0, 1, 2, 3, 4, 5].map(n => ({ id: n, name: n === 5 ? 'T5+' : 'T' + n }));

/**
 * O seletor de imóveis do chat (2026-10-27), dentro do modal "Enviar imóvel" e
 * do "Trocar" das visitas/propostas:
 *  - três âmbitos (2026-10-28): **os meus imóveis** (`showMyProperties`, em
 *    que sou agente principal), **a minha agência** (`showStoreProperties`,
 *    por omissão) e **toda a rede** (`showNetworkProperties`), este só com
 *    VIEW_PROPERTIES_NETWORK (`canSeeNetwork`). Para o MASTER a agência já é
 *    a entidade inteira: "agência" não leva flag e "rede" não existe. Quem
 *    abre o seletor escolhe o âmbito inicial (`initialScope`): o "Ver e
 *    enviar" da qualificação abre no mais largo, onde contou os imóveis;
 *  - **só imóveis disponíveis** e ativos (`onlyAvailable` + `status`): nunca
 *    vendidos, arrendados, reservados, cancelados, retirados ou rascunhos;
 *  - filtros: localização (distrito → concelho), tipo, negócio, tipologia,
 *    preço e pesquisa por referência ou título; 10 de cada vez, "Mostrar mais";
 *  - a qualificação do cliente chega como `preset` (os tipos do pedido ficam
 *    numa etiqueta que se pode tirar).
 * As opções vêm de `GET /get-form-properties/tab-all` (as da rede, de
 * `/get-form-properties/districts`), como na lista de imóveis.
 */
@Component({
  selector: 'chat-property-picker',
  templateUrl: './chat-property-picker.component.html',
  styleUrls: ['./chat-property-picker.component.css']
})
export class ChatPropertyPickerComponent implements OnInit, OnDestroy {
  /** 'send': vários imóveis; 'confirm': um só (o da visita ou da proposta). */
  @Input() mode: 'send' | 'confirm' = 'send';
  @Input() preset: ChatPropertyPickerPreset | null = null;
  /** Ids que já vêm marcados ("Ver e enviar"). */
  @Input() preselect: string[] = [];
  /** Base das fotos dos imóveis (o mesmo `pathPropertyImage` do chat). */
  @Input() imageBase = '';
  @Input() canSeeNetwork = false;
  /** MASTER: "agência" é a entidade inteira (sem flag) e não há "rede". */
  @Input() master = false;
  /** O âmbito com que abre; 'network' sem direito a ele cai para 'store'. */
  @Input() initialScope: ChatPropertyScope = 'store';
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;
  @Output() selectedChange = new EventEmitter<ChatPropertyPick[]>();

  scope: ChatPropertyScope = 'store';
  query = '';
  filtersOpen = false;
  districts: number[] = [];
  counties: number[] = [];
  propertyTypes: number[] = [];
  businessTypes: number[] = [];
  typology: number[] = [];
  priceMin: number | null = null;
  priceMax: number | null = null;
  /** Os tipos (grupos) da qualificação: só vão no pedido enquanto a etiqueta lá estiver. */
  presetTypeGroups: number[] = [];

  districtOptions: any[] = [];
  countyOptions: Option[] = [];
  propertyTypeOptions: any[] = [];
  businessTypeOptions: any[] = [];
  readonly typologyOptions = TYPOLOGY_OPTIONS;

  results: ChatPropertyPick[] = [];
  total = 0;
  page = 1;
  loading = false;
  loadingMore = false;
  selected: ChatPropertyPick[] = [];

  private storeDistricts: any[] = [];
  private networkDistricts: any[] | null = null;
  private search$ = new Subject<void>();
  private subscriptions = new Subscription();
  private pendingPreselect: string[] = [];

  constructor(private propertiesService: PropertiesService) {}

  ngOnInit(): void {
    this.scope = this.initialScope === 'network' && !this.hasNetworkScope ? 'store' : this.initialScope;
    this.applyPreset(this.preset);
    this.pendingPreselect = (this.preselect || []).map(String);
    this.subscriptions.add(this.search$.pipe(
      debounceTime(300),
      switchMap(() => {
        this.page = 1;
        return this.propertiesService.getAll(this.body(1)).pipe(catchError(() => of({ properties: [], total: 0 })));
      })
    ).subscribe((data: any) => {
      this.loading = false;
      this.results = ((data && data.properties) || []).map((p: any) => this.toPick(p));
      this.total = Number((data && data.total) || 0);
      this.applyPreselect();
    }));
    this.subscriptions.add(this.propertiesService.getDataForAllTab().pipe(catchError(() => of(null))).subscribe((data: any) => {
      const details = data && data.details ? data.details : {};
      const real = (item: any) => item && item.description !== '---';
      this.propertyTypeOptions = (details.propertyTypes || []).filter(real);
      this.businessTypeOptions = (details.businessTypes || []).filter(real);
      this.storeDistricts = Array.isArray(details.locations) ? details.locations : [];
      if (this.scope !== 'network') {
        this.districtOptions = this.storeDistricts;
      }
    }));
    if (this.scope === 'network') {
      this.loadNetworkDistricts();
    }
    this.search();
  }

  /** "Toda a rede" existe fora do MASTER e com a permissão da lista de imóveis. */
  get hasNetworkScope(): boolean {
    return !this.master && this.canSeeNetwork;
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get activeFilters(): number {
    return [this.districts, this.counties, this.propertyTypes, this.businessTypes, this.typology]
      .filter(list => list.length > 0).length
      + (this.priceMin || this.priceMax ? 1 : 0)
      + (this.presetTypeGroups.length > 0 ? 1 : 0);
  }

  get hasMore(): boolean {
    return this.results.length < this.total;
  }

  search(): void {
    this.loading = true;
    this.search$.next();
  }

  onQuery(event: Event): void {
    this.query = ((event.target as HTMLInputElement).value || '').trim();
    this.search();
  }

  setScope(scope: ChatPropertyScope): void {
    if (scope === 'network' && !this.hasNetworkScope) {
      return;
    }
    this.scope = scope;
    // A localização da rede é a árvore inteira; a dos outros âmbitos, a do utilizador.
    if (scope === 'network') {
      this.loadNetworkDistricts();
    } else {
      this.districtOptions = this.storeDistricts;
    }
    this.search();
  }

  private loadNetworkDistricts(): void {
    if (this.networkDistricts) {
      this.districtOptions = this.networkDistricts;
      return;
    }
    this.subscriptions.add(this.propertiesService.getDistrictsByNetwork().pipe(catchError(() => of([]))).subscribe((data: any) => {
      this.networkDistricts = Array.isArray(data) ? data : [];
      if (this.scope === 'network') {
        this.districtOptions = this.networkDistricts;
      }
    }));
  }

  onDistrictsChange(): void {
    const chosen = this.districtOptions.filter((d: any) => this.districts.indexOf(d.id) > -1);
    this.countyOptions = [];
    chosen.forEach((d: any) => (d.counties || []).forEach((c: any) => this.countyOptions.push({ id: c.id, name: c.name })));
    this.counties = this.counties.filter(id => this.countyOptions.some(c => c.id === id));
    this.search();
  }

  removePresetTypes(): void {
    this.presetTypeGroups = [];
    this.search();
  }

  clearFilters(): void {
    this.districts = [];
    this.counties = [];
    this.countyOptions = [];
    this.propertyTypes = [];
    this.businessTypes = [];
    this.typology = [];
    this.priceMin = null;
    this.priceMax = null;
    this.presetTypeGroups = [];
    this.search();
  }

  loadMore(): void {
    if (!this.hasMore || this.loading || this.loadingMore) {
      return;
    }
    const next = this.page + 1;
    this.loadingMore = true;
    this.subscriptions.add(this.propertiesService.getAll(this.body(next)).pipe(catchError(() => of({ properties: [], total: this.total })))
      .subscribe((data: any) => {
        this.loadingMore = false;
        const more = ((data && data.properties) || []).map((p: any) => this.toPick(p));
        const known = new Set(this.results.map(r => String(r.id)));
        this.results = [...this.results, ...more.filter((p: ChatPropertyPick) => !known.has(String(p.id)))];
        this.page = next;
      }));
  }

  isSelected(p: ChatPropertyPick): boolean {
    return this.selected.some(x => String(x.id) === String(p.id));
  }

  toggle(p: ChatPropertyPick): void {
    if (this.mode === 'confirm') {
      this.selected = this.isSelected(p) ? [] : [p];
    } else {
      this.selected = this.isSelected(p)
        ? this.selected.filter(x => String(x.id) !== String(p.id))
        : [...this.selected, p];
    }
    this.selectedChange.emit(this.selected);
  }

  priceText(p: ChatPropertyPick): string {
    if (p.price === undefined || p.price === null || p.price === '') {
      return '';
    }
    const value = typeof p.price === 'number' ? String(Math.round(p.price)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : String(p.price);
    return value + ' ' + (p.currency || '€');
  }

  trackByPick = (_i: number, p: ChatPropertyPick) => String(p.id);

  /** O corpo de `POST /properties/all/:lang`. */
  private body(page: number): any {
    const body: any = {
      page,
      search: this.query || undefined,
      sortColumn: 'updated',
      sortDirection: 'desc',
      // Só imóveis ativos (publicados) e no estado "Disponível".
      status: true,
      onlyAvailable: true
    };
    if (this.scope === 'mine') {
      body.showMyProperties = true;
    } else if (this.scope === 'network' && this.hasNetworkScope) {
      body.showNetworkProperties = true;
    } else if (!this.master) {
      body.showStoreProperties = true;
    }
    if (this.districts.length > 0) {
      body.district = this.districts;
    }
    if (this.counties.length > 0) {
      body.county = this.counties;
    }
    if (this.propertyTypes.length > 0) {
      body.propertyTypes = this.propertyTypes;
    }
    if (this.presetTypeGroups.length > 0) {
      body.propertyTypeGroups = this.presetTypeGroups;
    }
    if (this.businessTypes.length > 0) {
      body.businessTypes = this.businessTypes;
    }
    if (this.typology.length > 0) {
      const values = new Set<number>();
      this.typology.forEach(n => (n >= 5 ? [5, 6, 7, 8, 9, 10] : [n]).forEach(v => values.add(v)));
      body.typology = Array.from(values).sort((a, b) => a - b);
    }
    if (this.priceMin || this.priceMax) {
      body.price = { min: this.priceMin || undefined, max: this.priceMax || undefined };
    }
    return body;
  }

  private applyPreset(preset: ChatPropertyPickerPreset | null): void {
    if (!preset) {
      return;
    }
    if (Array.isArray(preset.typology) && preset.typology.length > 0) {
      this.typology = Array.from(new Set(preset.typology.map(n => (n >= 5 ? 5 : n))));
    }
    if (preset.price) {
      this.priceMin = preset.price.min || null;
      this.priceMax = preset.price.max || null;
    }
    if (Array.isArray(preset.businessTypes)) {
      this.businessTypes = preset.businessTypes.slice();
    }
    if (Array.isArray(preset.propertyTypeGroups)) {
      this.presetTypeGroups = preset.propertyTypeGroups.slice();
    }
    this.filtersOpen = this.activeFilters > 0;
  }

  /** "Ver e enviar": os imóveis que encaixam já vêm marcados (na primeira página). */
  private applyPreselect(): void {
    if (this.pendingPreselect.length === 0) {
      return;
    }
    const wanted = this.pendingPreselect;
    this.pendingPreselect = [];
    this.selected = this.results.filter(p => wanted.indexOf(String(p.id)) > -1);
    if (this.selected.length > 0) {
      this.selectedChange.emit(this.selected);
    }
  }

  /** Uma linha de `PropertiesService.getAll` no formato do seletor. */
  private toPick(p: any): ChatPropertyPick {
    const preview = p && p.preview ? p.preview : {};
    const location = [preview.county, preview.district].filter((x: any) => !!x).join(' · ');
    return {
      id: p.id,
      reference: p.reference,
      title: p.title || preview.title,
      price: p.price,
      currency: p.currency,
      image: p.image ? (/^(https?:)?\//.test(String(p.image)) ? p.image : this.imageBase + p.image) : undefined,
      location: location || undefined
    };
  }
}
